/**
 * CVUpload Component
 * Handles CV/Resume file upload with drag & drop functionality
 */

import React, { useState, useRef } from 'react';
import { 
  Upload, 
  File, 
  X, 
  CheckCircle, 
  AlertCircle,
  FileText,
  Loader
} from 'lucide-react';
import { uploadCV } from '../services/cvService';
import { useAuth } from '../../../core/contexts/AuthContext';
import { toast } from 'react-hot-toast';

/**
 * CVUpload Component
 * 
 * @param {function} onUploadSuccess - Callback when upload succeeds
 * @param {function} onUploadError - Callback when upload fails
 * @param {boolean} multiple - Allow multiple file uploads
 * @returns {JSX.Element} CVUpload component
 */
const CVUpload = ({ 
  onUploadSuccess, 
  onUploadError, 
  multiple = false 
}) => {
  const { currentUser } = useAuth();
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileInputRef = useRef(null);

  // Allowed file types
  const allowedTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ];

  const allowedExtensions = ['.pdf', '.doc', '.docx'];

  // Handle file validation
  const validateFile = (file) => {
    const errors = [];

    // Check file type
    if (!allowedTypes.includes(file.type)) {
      errors.push('Invalid file type. Please upload PDF or Word document.');
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      errors.push('File size too large. Please upload a file smaller than 10MB.');
    }

    // Check file name
    if (file.name.length > 100) {
      errors.push('File name too long. Please rename your file.');
    }

    return errors;
  };

  // Handle file upload
  const handleFileUpload = async (files) => {
    if (!currentUser) {
      toast.error('Please log in to upload your CV');
      return;
    }

    const fileList = Array.from(files);
    
    for (const file of fileList) {
      const errors = validateFile(file);
      
      if (errors.length > 0) {
        toast.error(errors[0]);
        if (onUploadError) onUploadError(errors);
        continue;
      }

      const fileId = `${file.name}_${Date.now()}`;
      
      try {
        // Update progress
        setUploadProgress(prev => ({
          ...prev,
          [fileId]: { status: 'uploading', progress: 0 }
        }));

        // Upload file
        const result = await uploadCV(file, currentUser.uid, (progress) => {
          setUploadProgress(prev => ({
            ...prev,
            [fileId]: { status: 'uploading', progress }
          }));
        });

        // Update success state
        setUploadProgress(prev => ({
          ...prev,
          [fileId]: { status: 'completed', progress: 100 }
        }));

        setUploadedFiles(prev => [...prev, { ...result, originalName: file.name }]);
        
        toast.success(`${file.name} uploaded successfully!`);
        
        if (onUploadSuccess) onUploadSuccess(result);

      } catch (error) {
        console.error('Upload error:', error);
        
        setUploadProgress(prev => ({
          ...prev,
          [fileId]: { status: 'error', progress: 0 }
        }));

        toast.error(error.message || 'Failed to upload CV');
        if (onUploadError) onUploadError(error);
      }
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files);
    }
  };

  // File input handler
  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileUpload(files);
    }
  };

  // Open file dialog
  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  // Remove uploaded file from display
  const removeUploadedFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Get file icon
  const getFileIcon = (fileName) => {
    const extension = fileName.toLowerCase().split('.').pop();
    
    switch (extension) {
      case 'pdf':
        return <FileText className="w-6 h-6 text-red-500" />;
      case 'doc':
      case 'docx':
        return <File className="w-6 h-6 text-blue-500" />;
      default:
        return <File className="w-6 h-6 text-gray-500" />;
    }
  };

  return (
    <div className="w-full">
      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
          isDragging
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={allowedExtensions.join(',')}
          onChange={handleFileInputChange}
          multiple={multiple}
          className="hidden"
        />

        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Upload your CV/Resume
        </h3>
        
        <p className="text-gray-600 mb-4">
          Drag and drop your file here, or click to browse
        </p>
        
        <div className="text-sm text-gray-500">
          <p>Supported formats: PDF, DOC, DOCX</p>
          <p>Maximum file size: 10MB</p>
        </div>
      </div>

      {/* Upload Progress */}
      {Object.keys(uploadProgress).length > 0 && (
        <div className="mt-6 space-y-3">
          <h4 className="font-medium text-gray-900">Upload Progress</h4>
          {Object.entries(uploadProgress).map(([fileId, progress]) => {
            const fileName = fileId.split('_')[0];
            return (
              <div key={fileId} className="bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    {getFileIcon(fileName)}
                    <span className="ml-2 text-sm font-medium text-gray-900">
                      {fileName}
                    </span>
                  </div>
                  
                  <div className="flex items-center">
                    {progress.status === 'uploading' && (
                      <Loader className="w-4 h-4 text-blue-500 animate-spin mr-2" />
                    )}
                    {progress.status === 'completed' && (
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                    )}
                    {progress.status === 'error' && (
                      <AlertCircle className="w-4 h-4 text-red-500 mr-2" />
                    )}
                    <span className="text-sm text-gray-600">
                      {progress.status === 'uploading' && `${progress.progress}%`}
                      {progress.status === 'completed' && 'Complete'}
                      {progress.status === 'error' && 'Failed'}
                    </span>
                  </div>
                </div>
                
                {progress.status === 'uploading' && (
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${progress.progress}%` }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Uploaded Files */}
      {uploadedFiles.length > 0 && (
        <div className="mt-6 space-y-3">
          <h4 className="font-medium text-gray-900">Uploaded Files</h4>
          {uploadedFiles.map((file, index) => (
            <div key={index} className="bg-green-50 p-3 rounded-lg border border-green-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {getFileIcon(file.originalName)}
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">
                      {file.originalName}
                    </p>
                    <p className="text-xs text-gray-500">
                      Uploaded successfully
                    </p>
                  </div>
                </div>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeUploadedFile(index);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tips */}
      <div className="mt-6 bg-blue-50 p-4 rounded-lg">
        <h4 className="font-medium text-blue-900 mb-2">Tips for a great CV:</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Keep it concise and relevant (1-2 pages)</li>
          <li>• Use a professional format and clear structure</li>
          <li>• Include your contact information and skills</li>
          <li>• Highlight achievements with quantifiable results</li>
          <li>• Proofread for spelling and grammar errors</li>
        </ul>
      </div>
    </div>
  );
};

export default CVUpload;